'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { usePathname } from 'next/navigation';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

import { cn } from '@natu/utils/cn';
import type { PredefinedGAEvents } from '@natu/utils/events';
import { getEventPayload, type EventProps } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = forwardRef<
  ElementRef<typeof AccordionPrimitive.Item>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('data-[disabled]:cursor-not-allowed data-[disabled]:opacity-30', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps
  extends ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  event?: PredefinedGAEvents;
  eventProps?: EventProps;
}

const AccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, event, eventProps, ...props }, ref) => {
  const pathname = usePathname();
  const { isCampaign } = usePageContext() || {};
  const eventPayload = event ? getEventPayload({ pathname, event, eventProps, isCampaign }) : {};

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cn(
          'group flex flex-1 items-center justify-between py-3',
          'ease-naturaily duration-600 transition-all',
          '[&[data-state=open]>svg]:rotate-180',
          'data-[disabled]:cursor-not-allowed',
          className,
        )}
        {...(event && { onClick: () => sendGTMEvent({ event, ...eventPayload }) })}
        {...props}
      >
        {children}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = forwardRef<
  ElementRef<typeof AccordionPrimitive.Content>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      '!ease-naturaily data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden transition-all !duration-500',
      className,
    )}
    {...props}
  >
    {children}
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
